/**
 * Maps database/property field names (as stored in AuditDtl.vFieldName) to UI display names
 * shown in forms. Used in Audit Report table and Excel export so users see familiar labels.
 * Add new mappings when new auditable fields or forms are introduced.
 */
const AUDIT_FIELD_TO_UI_LABEL = {
  // Site (SiteMst / Edit Site modal)
  ProjectCode: 'Project',
  SiteCode: 'Site',
  SiteDescription: 'Site Name',
  SiteEmailId: 'Site Email Id',
  GlobalSiteCode: 'Global Site Code',
  InvestigatorNo: 'Investigator No',
  CountryNo: 'Country',
  InvestigatorDesc: 'PI Name',
  InvestigatorMail: 'Investigator Email Id',
  CountryNiceName: 'Country',
  ECMailId: 'EC Email',
  RecruitmentProjections: 'No. Of Recruitment Projections',
  IsActive: 'Active',
  Frequency: 'Frequency',
  SiteVisits: 'Site Visits',
  NSiteVisits: 'Site Visits',
  TherapeuticArea: 'Therapeutic Area',
  Disease: 'Disease',
  UserId: 'CRA',

  // Table-specific overrides (see getAuditFieldDisplayName tableName param):
  // SiteCRAAssignments (vUserIds / CRA or legacy UserId) -> CRA; SiteCoMonitorAssignments (vUserIds / CoMonitor or legacy UserId) -> Co-Monitor

  // Visit Execution Report
  ReportStatus: 'Report Status',
  ReportVersion: 'Report Version',
  ReportContent: 'Report Content',
  SDVPercentageCompleted: 'SDV % Completed',
  SubmittedDate: 'Submitted Date',
  SubmittedBy: 'Submitted By',

  // User
  vUserName: 'User Name',
  vFirstName: 'First Name',
  vLastName: 'Last Name',
  vLoginName: 'Login Name',
  vUserTypeCode: 'User Type / Role',
  vDeptCode: 'Department',
  vLocationCode: 'Location',
  vEmailId: 'Email Id',
  vPhoneNo: 'Phone No',
  IsAllowADUser: 'Allow AD User',
  vADUserName: 'AD User Name',
  IsContractual: 'Contractual',
  IsMFA: 'MFA',
  vRemark: 'Remark',
  IsActive: 'Active',
  IsProjectUser: 'Project User',

  // CRF / DCF
  vDCFStatus: 'DCF Status',
  VDCFStatus: 'DCF Status',
  vDCFRemark: 'DCF Remark',
  VDCFRemark: 'DCF Remark',
  vDCFResponse: 'DCF Response',
  VDCFResponse: 'DCF Response',
  VDCFStageRaised: 'DCF Stage Raised',
  VDCFRaisedByProfileCode: 'DCF Raised By',
  VTemplateNo: 'Template',
  VVisitType: 'Visit Type',
  IVisitNodeId: 'Visit',
  IsRejected: 'Rejected',
  CSaveType: 'Save Type',
  CActiveFlag: 'Active',
  CReplicaFlag: 'Replica',
  VStage: 'Stage',
  VFieldId: 'Question',
  VFieldValue: 'Answer',
  VComment: 'Comment',

  // Monitoring Visit & Report (Schedule/Edit Visit, Confirm PI)
  Notes: 'Notes',
  ConfirmationMode: 'Confirmation Mode',
  ConfirmedOn: 'Confirmed On',
  'Confirmed On': 'Confirmed On',
  'Rejected On': 'Rejected On',
  ConfirmationSentToUserID: 'Confirmation Sent To',

  // Visit / Letter
  LetterType: 'Letter Type',
  LetterVersion: 'Letter Version',
  PIEmail: 'PI Email',
  OTPRequired: 'OTP Required',
  LetterStatus: 'Letter Status',

  // SSV / common
  vSSVSiteCode: 'SSV Site Code',
  SSVSiteCode: 'SSV Site Code',

  // User role project site mapping
  vProjectCode: 'Project Code',
  vSiteCode: 'Site Code',
};

/**
 * Returns the UI display name for an audit field (database property name).
 * When tableName is provided, table-specific overrides apply: e.g. CoMonitor / UserId under SiteCoMonitorAssignments -> "Co-Monitor"; CRA / vUserIds under SiteCRAAssignments -> "CRA".
 * Falls back to a humanized version (e.g. CountryNiceName -> "Country Nice Name") if not in the map.
 * @param {string} fieldName - Value from AuditDtl.vFieldName (e.g. "CountryNiceName", "UserId")
 * @param {string} [tableName] - Optional AuditHdr.vTableName (e.g. "SiteCoMonitorAssignments", "SiteCRAAssignments") for table-aware labels
 * @returns {string} UI-friendly label
 */
export function getAuditFieldDisplayName(fieldName, tableName) {
  if (fieldName == null || String(fieldName).trim() === '') return '';
  const key = String(fieldName).trim();
  const tbl = tableName != null ? String(tableName).trim() : '';
  if (tbl === 'SiteCoMonitorAssignments' && (key === 'UserId' || key === 'CoMonitor' || key === 'UserIds')) return 'Co-Monitor';
  if (tbl === 'SiteCRAAssignments' && (key === 'UserId' || key === 'CRA')) return 'CRA';
  if (AUDIT_FIELD_TO_UI_LABEL[key]) return AUDIT_FIELD_TO_UI_LABEL[key];
  // Try without leading 'v' (some stores might use column name)
  const withoutV = key.startsWith('v') && key.length > 1 ? key.slice(1) : key;
  if (AUDIT_FIELD_TO_UI_LABEL[withoutV]) return AUDIT_FIELD_TO_UI_LABEL[withoutV];
  // Humanize: insert space before capitals and trim (e.g. ReportStatus -> Report Status)
  const humanized = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/^v/, '')
    .trim();
  return humanized || key;
}
